import { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import api from '../api/api';

const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

export default function HourometerModal({ equipment, onClose, onSaved }) {
  const now = new Date();
  const [form, setForm] = useState({
    equipment_id: equipment.id,
    month: now.getMonth() + 1,
    year: now.getFullYear(),
    hours_month: 0,
    hours_accumulated_annual: 0,
    hours_start_month: equipment.total_hours || 0,
    hours_total_machine: equipment.total_hours || 0,
    km_month: '',
    km_accumulated_annual: '',
    km_total_equipment: '',
    km_start_month: '',
    fuel: '',
    oil_motor: '',
    oil_hydraulic: '',
    oil_transmission: '',
    oil_reducer: '',
    observations: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isMobile = equipment.type === 'mobile';

  const handleChange = (key, value) => {
    const updated = { ...form, [key]: value };
    // Auto-calculate total hours
    if (key === 'hours_month' || key === 'hours_start_month') {
      const start = parseFloat(updated.hours_start_month) || 0;
      const month = parseFloat(updated.hours_month) || 0;
      updated.hours_total_machine = start + month;
    }
    setForm(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const data = { ...form };
      // Convert empty strings to null for optional numeric fields
      ['km_month','km_accumulated_annual','km_total_equipment','km_start_month','fuel','oil_motor','oil_hydraulic','oil_transmission','oil_reducer'].forEach(k => {
        if (data[k] === '' || data[k] === undefined) data[k] = null;
        else data[k] = parseFloat(data[k]);
      });
      ['hours_month','hours_accumulated_annual','hours_start_month','hours_total_machine'].forEach(k => {
        data[k] = parseFloat(data[k]) || 0;
      });
      await api.post('/hourometer', data);
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>
              Registrar Horómetro
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">{equipment.name} · {equipment.code}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm" data-testid="hourometer-error">
              {error}
            </div>
          )}

          {/* Period */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Mes</label>
              <select
                value={form.month}
                onChange={e => handleChange('month', parseInt(e.target.value))}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none bg-white"
                data-testid="hourometer-month"
              >
                {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Año</label>
              <select
                value={form.year}
                onChange={e => handleChange('year', parseInt(e.target.value))}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none bg-white"
                data-testid="hourometer-year"
              >
                {[2026, 2025, 2024, 2023].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          {/* Hours section */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3">Horas de Funcionamiento</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Horas Inicio/Mes', key: 'hours_start_month', testId: 'hours-start' },
                { label: 'Horas Funcionamiento Mes', key: 'hours_month', testId: 'hours-month' },
                { label: 'Horas Acumulado Anual', key: 'hours_accumulated_annual', testId: 'hours-accum' },
                { label: 'Horas Total Máquina', key: 'hours_total_machine', testId: 'hours-total', readOnly: true },
              ].map(f => (
                <div key={f.key}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={form[f.key]}
                    onChange={e => handleChange(f.key, e.target.value)}
                    readOnly={f.readOnly}
                    className={`w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none font-mono
                      ${f.readOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'}`}
                    data-testid={f.testId}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* KM & Fluids for mobile */}
          {isMobile && (
            <div className="bg-blue-50 rounded-lg p-4 mb-4">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3">Kilómetros y Fluidos</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'KM Inicio Mes', key: 'km_start_month' },
                  { label: 'KM/Mes', key: 'km_month' },
                  { label: 'KM Acumulado Anual', key: 'km_accumulated_annual' },
                  { label: 'KM Total Equipo', key: 'km_total_equipment' },
                  { label: 'Combustible (L)', key: 'fuel' },
                  { label: 'Aceite Motor (L)', key: 'oil_motor' },
                  { label: 'Aceite Hidráulico (L)', key: 'oil_hydraulic' },
                  { label: 'Aceite Transmisión (L)', key: 'oil_transmission' },
                ].map(f => (
                  <div key={f.key}>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form[f.key]}
                      onChange={e => handleChange(f.key, e.target.value)}
                      placeholder="—"
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none bg-white font-mono"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fixed machinery oils */}
          {!isMobile && (
            <div className="bg-blue-50 rounded-lg p-4 mb-4">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3">Aceites</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Aceite Hidráulico (L)', key: 'oil_hydraulic' },
                  { label: 'Aceite Reductor (L)', key: 'oil_reducer' },
                ].map(f => (
                  <div key={f.key}>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
                    <input
                      type="number" step="0.01" min="0"
                      value={form[f.key]}
                      onChange={e => handleChange(f.key, e.target.value)}
                      placeholder="—"
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none bg-white font-mono"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Observations */}
          <div className="mb-5">
            <label className="block text-xs font-medium text-gray-700 mb-1">Observaciones</label>
            <textarea
              value={form.observations}
              onChange={e => handleChange('observations', e.target.value)}
              rows={2}
              placeholder="Averías, reparaciones realizadas, notas..."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none resize-none"
              data-testid="hourometer-observations"
            />
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-[#16a34a] text-white rounded-lg hover:bg-[#15803d] transition-colors"
              data-testid="save-hourometer-btn"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              {saving ? 'Guardando...' : 'Guardar Registro'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
